/** @jsxImportSource @emotion/react */
import * as yup from "yup";
import { css } from "@emotion/react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { useNavigate } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  AdyenAccountHolder,
  AdyenAccountHolderFormType,
} from "../models/adyen-account-holder";
import * as authSelectors from "../../auth/authSelectors";
import { colors } from "../../../components/colors";
import {
  useCreateAdyenAccountHolder,
  usePushAdyenAccountHolderToAdyen,
  useUpdateAdyenAccountHolder,
} from "../service/adyen-account-holders-api";
import { useGetCountries, useGetOrganizations } from "../../../service/api";
import { SpinnerBlock } from "../../common/Spinner";
import { FormRow } from "../../common/hook-form";
import { TextInput } from "../../../components/forms/inputs/TextInput";
import { getErrorMessage } from "../../../error-handler";
import { SelectInput } from "../../../components/forms/inputs/SelectInput";
import { ActionSpinner } from "../../../components/ActionSpinner";
import {
  ButtonAction,
  ButtonInternalLink,
} from "../../../components/NewButtons";
import { getCountrySpecificValues } from "../../../util";
import { useEffect } from "react";
import { SelectCountryInput } from "../../../components/forms/inputs/SelectCountryInput";
import { TextAreaInput } from "../../../components/forms/inputs/TextAreaInput";

export const buttonWrapperStyle = css`
  position: sticky;
  bottom: 0;
  z-index: 100;
  background-color: ${colors.white};
  display: flex;
  flex-direction: row;
  border-radius: 0px 0px 8px 8px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.greyBorder};
  gap: 8px;
  width: 100%;
  align-items: center;
  height: fit-content;
  margin-top: auto;
  justify-content: end;
`;

export const formStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  overflow-y: auto;
`;

export const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-top: 24px;
  padding-bottom: 24px;
  width: 600px;
`;

export const bodySectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

export const bodyHeaderStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

type Props = {
  adyenAccountHolder?: AdyenAccountHolder;
};

export const MutateAdyenAccountHolderForm = ({ adyenAccountHolder }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const { timezone, countryCode } = getCountrySpecificValues();
  const { data: countries, isLoading: isLoadingCountries } = useGetCountries();

  const navigate = useNavigate();

  const schema = yup.object({
    description: yup.string().required("Description is required"),
    legalName: yup.string().when("adyenAccountHolder", {
      is: (adyenAccountHolder: AdyenAccountHolder) => !!adyenAccountHolder,
      then: yup.string().required("Legal name is required"),
      otherwise: yup.string(),
    }),
    countryCode: yup.string().when("adyenAccountHolder", {
      is: (adyenAccountHolder: AdyenAccountHolder) => !!adyenAccountHolder,
      then: yup.string().required("Country code is required"),
      otherwise: yup.string(),
    }),
    timezone: yup.string().required("Timezone is required"),
  });

  const methods = useForm<AdyenAccountHolderFormType>({
    defaultValues: {
      description: adyenAccountHolder?.description || "",
      legalName: adyenAccountHolder?.legalName || "",
      countryCode: adyenAccountHolder?.countryCode || countryCode,
      timezone: adyenAccountHolder
        ? adyenAccountHolder.timezone
          ? adyenAccountHolder.timezone
          : ""
        : timezone,
      pushToAdyen: true,
      organizationId: adyenAccountHolder?.organizationId?.toString() || "",
    },
    resolver: yupResolver(schema),
  });

  const { reset, getValues } = methods;

  const {
    data: organizations,
    isLoading: isLoadingOrganizations,
  } = useGetOrganizations(organizationKeyName);

  const {
    mutateAsync: createAdyenAccountHolder,
  } = useCreateAdyenAccountHolder();

  const {
    mutateAsync: pushAdyenAccountHolderToAdyen,
  } = usePushAdyenAccountHolderToAdyen(adyenAccountHolder?.id);

  const { mutateAsync: updateAdyenAccountHolder } = useUpdateAdyenAccountHolder(
    adyenAccountHolder?.id
  );

  const onSubmit = async (data: AdyenAccountHolderFormType) => {
    try {
      if (adyenAccountHolder) {
        await updateAdyenAccountHolder({
          data: {
            description: data.description,
            timezone: data.timezone,
            pushToAdyen: data.pushToAdyen,
            organizationId: data.organizationId
              ? Number(data.organizationId)
              : null,
            id: adyenAccountHolder.id,
          },
        });
        await pushAdyenAccountHolderToAdyen({
          data: { id: adyenAccountHolder.id },
        });
      } else {
        await createAdyenAccountHolder({
          data: {
            ...data,
            organizationId: Number(data.organizationId),
          },
        });
      }
      toastr.success(
        "Success",
        `Adyen account holder ${
          adyenAccountHolder ? "updated" : "created"
        } successfully`
      );
      navigate("..");
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  const timezoneOptions = Array.from(
    new Set(
      (countries || []).filter((c) => c.enabled).map((c) => c.defaultTimezone)
    )
  ).map((timezone) => ({
    label: timezone,
    value: timezone,
  }));

  useEffect(() => {
    if (isLoadingOrganizations || !organizations) {
      return;
    }

    const org = organizations.find((o) => o.keyName === organizationKeyName);
    if (org && !adyenAccountHolder) {
      reset({
        ...getValues(),
        organizationId: org.id,
      });
    }
  }, [
    reset,
    getValues,
    organizations,
    isLoadingOrganizations,
    organizationKeyName,
    adyenAccountHolder,
  ]);

  if (isLoadingOrganizations || isLoadingCountries) {
    return <SpinnerBlock />;
  }

  return (
    <FormProvider {...methods}>
      <form css={formStyle} onSubmit={methods.handleSubmit(onSubmit)}>
        <div css={bodyStyle}>
          <div css={bodySectionStyle}>
            {!adyenAccountHolder && (
              <FormRow>
                <TextInput
                  name="legalName"
                  label="Legal name"
                  placeholder="Enter the legal name of the account holder"
                  helperText="You cannot change the legal name later."
                />
              </FormRow>
            )}
            <FormRow>
              <TextAreaInput
                name="description"
                label="Description"
                placeholder="Describe the account holder"
                helperText="You can change the description later."
              />
            </FormRow>
            {!adyenAccountHolder && (
              <FormRow>
                <SelectCountryInput name="countryCode" />
              </FormRow>
            )}
            <FormRow>
              <SelectInput
                name="organizationId"
                label="Organization"
                helperText="You can change the organization later."
                options={(organizations || []).map((o) => ({
                  label: o.name,
                  value: o.id,
                }))}
              />
            </FormRow>
            <FormRow>
              <SelectInput
                name="timezone"
                label="Timezone"
                helperText="You can change the timezone later."
                options={timezoneOptions}
              />
            </FormRow>
          </div>
        </div>
        <div css={buttonWrapperStyle}>
          <ButtonInternalLink to={".."}>Cancel</ButtonInternalLink>
          <ButtonAction type="submit" disabled={methods.formState.isSubmitting}>
            {methods.formState.isSubmitting ? (
              <ActionSpinner />
            ) : adyenAccountHolder ? (
              "Update account holder"
            ) : (
              "Create account holder"
            )}
          </ButtonAction>
        </div>
      </form>
    </FormProvider>
  );
};
